import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import PdfPages from './PdfPages';

function PreviewModal({ file, fileUrl, isOpen, onClose, isPdf = false }) {
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Set up keyboard listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !fileUrl) return null;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file?.name || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenExternal = () => {
    window.open(fileUrl, '_blank');
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking on the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Mobile: full-screen viewer; PDFs are drawn with pdf.js because iOS
  // can't render <object type="application/pdf">
  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[65] bg-gray-900 flex flex-col" role="dialog" aria-modal="true" aria-label="File preview">
        <div className="flex items-center justify-between px-2 pb-2 pt-safe text-white">
          <button onClick={onClose} className="p-2 rounded-full active:bg-white/10" aria-label="Close preview">
            <X className="w-6 h-6" />
          </button>
          <span className="text-sm font-medium">{isPdf ? 'PDF' : 'Receipt'}</span>
          <button onClick={handleOpenExternal} className="p-2 rounded-full active:bg-white/10" aria-label="Open">
            <ExternalLink className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-3 pb-safe">
          {isPdf ? (
            <PdfPages source={fileUrl} />
          ) : (
            <div className="min-h-full flex items-center justify-center">
              <img src={fileUrl} alt="Full preview" className="max-w-full object-contain rounded" />
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex flex-col items-center justify-center p-2 sm:p-4" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="File preview"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl max-h-[95vh] w-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isPdf ? 'PDF Preview' : 'Image Preview'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
            aria-label="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4 min-h-[70vh]">
          {isPdf ? (
            <div className="w-full h-full min-h-[65vh] bg-white rounded">
              <object
                data={`${fileUrl}#view=FitH`}
                type="application/pdf"
                className="w-full h-full min-h-[65vh] rounded"
                onLoad={() => setIsLoading(false)}
              >
                <p className="p-4 text-center text-gray-500">
                  Unable to display PDF. <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Open in new tab</a>
                </p>
              </object>
            </div>
          ) : (
            <img
              src={fileUrl}
              alt="Full preview"
              className="max-w-full max-h-[75vh] object-contain rounded"
              onLoad={() => setIsLoading(false)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleOpenExternal}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Open in new tab</span>
            <span className="sm:hidden">Open</span>
          </button>
          <button
            onClick={handleDownload}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
            <span className="sm:hidden">Save</span>
          </button>
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default PreviewModal;
